/**
 * `<pg-modal>` — janela modal reutilizável.
 *
 * Atributo `open`: presença controla se o modal está visível. O
 * componente NUNCA decide sozinho fechar-se de forma definitiva — ele
 * apenas emite `pg-close` (ao clicar no overlay, no botão de fechar, ou
 * pressionar Escape); quem decide remover o atributo `open` (ou não) é
 * a tela que consome o componente, mantendo a fonte de verdade do
 * estado sempre na aplicação, nunca no componente.
 *
 * Slots: `header`, padrão (corpo), `footer`.
 *
 * Uso:
 * ```html
 * <pg-modal open>
 *   <span slot="header">Confirmar exclusão</span>
 *   <p>Esta ação não pode ser desfeita.</p>
 *   <div slot="footer">
 *     <pg-button variant="ghost">Cancelar</pg-button>
 *     <pg-button variant="danger">Excluir</pg-button>
 *   </div>
 * </pg-modal>
 * ```
 */
export class PgModal extends HTMLElement {
    static get observedAttributes() {
        return ['open'];
    }
    root;
    aoTeclar = (evento) => {
        if (evento.key === 'Escape' && this.hasAttribute('open')) {
            this.emitirFechar();
        }
    };
    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
    }
    connectedCallback() {
        this.render();
        document.addEventListener('keydown', this.aoTeclar);
    }
    disconnectedCallback() {
        document.removeEventListener('keydown', this.aoTeclar);
    }
    attributeChangedCallback() {
        this.render();
    }
    emitirFechar() {
        this.dispatchEvent(new CustomEvent('pg-close', { bubbles: true, composed: true }));
    }
    render() {
        const aberto = this.hasAttribute('open');
        this.root.innerHTML = `
      <style>
        :host {
          display: ${aberto ? 'block' : 'none'};
        }
        .overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal {
          background: var(--pg-color-bg-elevado, #1a1d21);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-lg, 16px);
          box-shadow: var(--pg-shadow-lg, 0 12px 32px rgba(0,0,0,0.45));
          color: var(--pg-color-texto-primario, #f2f3f5);
          font-family: var(--pg-font-familia-base, system-ui);
          min-width: 320px;
          max-width: min(560px, 90vw);
          max-height: 85vh;
          overflow-y: auto;
        }
        .header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--pg-space-5, 24px) var(--pg-space-5, 24px) 0;
          font-size: var(--pg-font-size-lg, 1.125rem);
          font-weight: var(--pg-font-weight-semibold, 600);
        }
        .fechar {
          background: none;
          border: none;
          border-radius: var(--pg-radius-sm, 6px);
          color: var(--pg-color-texto-secundario, #9aa1ab);
          cursor: pointer;
          font-size: var(--pg-font-size-lg, 1.125rem);
          line-height: 1;
          padding: var(--pg-space-2, 8px);
        }
        .fechar:hover { color: var(--pg-color-texto-primario, #f2f3f5); }
        .fechar:focus-visible {
          outline: none;
          box-shadow: var(--pg-shadow-foco, 0 0 0 3px rgba(47,140,255,0.35));
        }
        .body {
          padding: var(--pg-space-5, 24px);
        }
        .footer {
          display: flex;
          justify-content: flex-end;
          gap: var(--pg-space-3, 12px);
          padding: 0 var(--pg-space-5, 24px) var(--pg-space-5, 24px);
        }
      </style>
      <div class="overlay" part="overlay">
        <div class="modal" part="modal" role="dialog" aria-modal="true">
          <div class="header">
            <slot name="header"></slot>
            <button class="fechar" part="fechar" aria-label="Fechar">&times;</button>
          </div>
          <div class="body"><slot></slot></div>
          <div class="footer"><slot name="footer"></slot></div>
        </div>
      </div>
    `;
        this.root.querySelector('.overlay')?.addEventListener('click', (evento) => {
            if (evento.target === this.root.querySelector('.overlay'))
                this.emitirFechar();
        });
        this.root.querySelector('.fechar')?.addEventListener('click', () => this.emitirFechar());
    }
}
customElements.define('pg-modal', PgModal);
//# sourceMappingURL=pg-modal.js.map